/*
 *   File : sip-mini-rebalance.js
 *   Author : https://evoqins.com
 *   Description : SIP Mini Rebalance modal component
 *   Version : 1.1.0
*/

import React, { useEffect, useRef, useState } from "react";

// Components
import { Toast } from '../note';
import SelectBox from "../dropdown/selectBox";
import { Input } from "../input";
import { PrimaryBtn, SecondaryBtn } from "../cta";

import style from '../../styles/components/rebalance.module.scss';
import devconfig from '../../config/config';
import APIPost from '../../services/postApi';
import { useNavigate } from "react-router-dom";
import { Modal } from "bootstrap";
import OverrideConfirmation from "./override-confirmation";

const sipActionOptions = [
    { label: "Edit", value: "EDIT" },
    { label: "Cancel", value: "CANCEL" }
];


// ─── Single SIP Block ────────────────────────────────────────────────────────
const SIPBlock = React.memo(({ sip, sipIndex, onFundsChange, onAddNewFund, toastRef }) => {

    const searchRef = useRef([]);
    const [fundsList, setFundsList] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        document.addEventListener('mouseup', _closeMenu);
        return () => document.removeEventListener('mouseup', _closeMenu);
        // eslint-disable-next-line
    }, [showDropdown]);

    const _closeMenu = (event) => {
        if (
            searchRef.current[showDropdown] &&
            !searchRef.current[showDropdown].contains(event.target)
        ) {
            setShowDropdown(false);
        }
    };

    const _getFunds = (query, index) => {
        const api_url = devconfig.admin + '/fund/v2/search';
        const sample_query = query.length > 0 ? query.replace(/\s*-\s*/g, " ") : "";
        const data = JSON.stringify({
            query: sample_query.toLowerCase().trim(),
            page_num: 1,
        });
        APIPost(api_url, data).then((response) => {
            if (response.status_code === 200) {
                setShowDropdown(index);
                setFundsList(response.data);
            } else {
                toastRef({ type: 'error', message: response.message });
                setShowDropdown(null);
                setFundsList([]);
            }
        });
    };

    const _getFundDetail = (fund, fundIndex) => {
        const api_url = devconfig.admin + '/fund/custom-fund';
        const data = JSON.stringify({ isin: fund.isin });
        APIPost(api_url, data).then((response) => {
            if (response.status_code === 200) {
                _handleSwitchFundSelect(fund.scheme_name, fundIndex, fund.scheme_code);
                setShowDropdown(false);
            } else {
                _handleSwitchFundSelect("", fundIndex, null);
                toastRef({ type: 'error', message: response.message });
            }
        });
    };

    function _handleAction(selected, fundIndex) {
        const normalized =
            typeof selected === "string"
                ? sipActionOptions.find(opt => opt.value === selected)
                : selected;

        const updated = sip.sip_funds.map((f, i) => {
            if (i !== fundIndex) return f;
            return { ...f, type: normalized, isEdited: true };
        });

        onFundsChange(sipIndex, updated);
    }

    // function _handleAmount(value, fundIndex) {
    //     const parsedValue = value === "" ? "" : parseFloat(value);

    //     const updated = sip.sip_funds.map((f, i) =>
    //         i === fundIndex
    //             ? {
    //                 ...f,
    //                 amount: isNaN(parsedValue) ? "" : parsedValue,
    //                 isEdited: true
    //             }
    //             : f
    //     );

    //     onFundsChange(sipIndex, updated);
    // }

    function _handleAmount(value, fundIndex) {
        const parsedValue = value === "" ? "" : parseFloat(value);

        const updated = sip.sip_funds.map((f, i) =>
            i === fundIndex
                ? {
                    ...f,
                    amount: isNaN(parsedValue) ? "" : parsedValue,
                    isEdited: true
                }
                : f
        );


        onFundsChange(sipIndex, updated);
    }

    function _handleSwitchFundSelect(value, fundIndex, switchId) {
        const updated = sip.sip_funds.map((f, i) =>
            i === fundIndex ? { ...f, switch_name: value, switch_in_mf_id: switchId } : f
        );
        onFundsChange(sipIndex, updated);
    }

    function _handleNewPurchaseFund(value, fundIndex, schemeCode) {
        const updated = sip.sip_funds.map((f, i) => {
            if (i !== fundIndex) return f;

            return {
                ...f,
                fund_name: value,
                mf_id: schemeCode, // map correctly
                isEdited: true,
                isNew: true
            };
        });

        onFundsChange(sipIndex, updated);
    }



    function _handleDeleteNewFund(fundIndex) {
        const updated = sip.sip_funds.filter((_, i) => i !== fundIndex);
        onFundsChange(sipIndex, updated);
    }

    // Shared fund-search dropdown renderer
    function _renderDropdown(fundIndex, onSelect) {
        if (showDropdown !== fundIndex) return null;
        const rect = searchRef.current[fundIndex]?.getBoundingClientRect();
        if (!rect) return null;
        return (
            <div
                className="e-cart-search-dropdown"
                style={{
                    position: "fixed",
                    top: rect.bottom,
                    left: rect.left,
                    zIndex: 9999,
                    maxHeight: "200px",
                    overflowY: "auto",
                    width: "250px",
                    wordWrap: "break-word",
                }}
            >
                {fundsList.map((fund, key) => (
                    <p
                        key={key}
                        onClick={() => onSelect(fund)}
                        style={{ margin: 0, padding: "8px", cursor: "pointer" }}
                    >
                        {fund.scheme_name}
                    </p>
                ))}
            </div>
        );
    }

    // Debounced search trigger
    function _triggerSearch(value, fundIndex) {
        if (!window.searchTimeouts) window.searchTimeouts = {};
        clearTimeout(window.searchTimeouts[`${sipIndex}-${fundIndex}`]);
        if (value.length > 0) {
            window.searchTimeouts[`${sipIndex}-${fundIndex}`] = setTimeout(() => {
                _getFunds(value, fundIndex);
            }, 500);
        } else {
            setShowDropdown(null);
        }
    };

    function getOrdinal(n) {
        n = parseInt(n); // in case it's string

        if (n > 3 && n < 21) return n + "th";

        switch (n % 10) {
            case 1: return n + "st";
            case 2: return n + "nd";
            case 3: return n + "rd";
            default: return n + "th";
        }
    }

    return (
        <>

            {sip.sip_id !== null && (
                <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                    <div className="sip-count-badge">
                        <h3 className="mb-0">{`SIP ${sipIndex + 1}`}</h3>
                    </div>


                    {sip.sip_amount != null && (
                        <div className="d-flex align-items-center gap-2">
                            <img
                                src={require("../../assets/images/modal/total-amount.svg").default}
                                alt="total-amount"
                                className="ms-2"
                            />
                            <span className={`${style.e_sip_meta} `}>
                                Total SIP: ₹{sip.sip_amount?.toLocaleString('en-IN')}
                            </span>
                        </div>
                    )}
                    {sip.sip_date && (
                        <div className="d-flex align-items-center gap-2">
                            <img
                                src={require("../../assets/images/modal/sip-calendar.svg").default}
                                alt="calendar"
                                className="ms-2"
                            />
                            <span className={style.e_sip_meta}>
                                SIP date: {sip.sip_date_text}
                            </span>
                        </div>
                    )}
                    {sip.next_due && (
                        <div className="d-flex align-items-center gap-2">
                            <img
                                src={require("../../assets/images/modal/clock.svg").default}
                                alt="clock"
                                className="ms-2"
                            />
                            <span className={style.e_sip_meta}>
                                Next SIP: {sip.next_due}
                            </span>
                        </div>
                    )}
                </div>
            )}


            <div className={`${style.e_investment_wrapper} mb-4`}>


                {/* Column headers */}
                <div className='row gx-2 mb-1'>
                    <div className='col-3'><h6>Invested Funds</h6></div>
                    <div className='col-1'><h6>Allocation</h6></div>
                    <div className='col-1'><h6>Amount</h6></div>
                    <div className='col-7'><h6>Action</h6></div>
                </div>

                {/* Fund rows */}
                {/* {sip.sip_funds.map((fundData, fundIndex) => {
                    const actionValue =
                        typeof fundData.type === "string"
                            ? fundData.type
                            : fundData.type?.value;

                    return (
                        <div className='row gx-2 align-items-center mb-1' key={fundIndex}>

                            <div className='col-3'>
                                <p className='pt-2 mb-0'>{fundData.fund_name}</p>
                            </div>

                            <div className='col-1'>
                                <p className='pt-2 mb-0'>{fundData.allocation ?? "0"}%</p>
                            </div>

                            <div className='col-1'>
                                <p className='pt-2 mb-0'>
                                    {!isNaN(fundData.amount) && fundData.amount !== ""
                                        ? `₹${fundData.amount}`
                                        : "-"}
                                </p>
                            </div>

                            <div className='col-2 position-relative pt-2'>
                                {!fundData.isNew && (
                                    <SelectBox
                                        options={sipActionOptions}
                                        value={fundData.type}
                                        isSearchable={false}
                                        minHeight="38px"
                                        placeholder="No Action Required"
                                        selectchange={(value) => _handleAction(value, fundIndex)}
                                    />
                                )}
                            </div>

                            <div className='col-5 pt-2'>
                                <div className="d-flex gap-3 align-items-center">

                                    {fundData.isNew && (
                                        <>
                                            <div
                                                ref={(el) => (searchRef.current[fundIndex] = el)}
                                                className="position-relative"
                                            >
                                                <Input
                                                    type="text"
                                                    placeholder="Search for fund"
                                                    className="mt-0 p-2 w-300px"
                                                    value={fundData.fund_name} //  updated
                                                    valueChange={(value) => {
                                                        _handleNewPurchaseFund(value, fundIndex, null);
                                                        _triggerSearch(value, fundIndex);
                                                    }}
                                                />

                                                {_renderDropdown(fundIndex, (fund) => {
                                                    setShowDropdown(null);

                                                    _handleNewPurchaseFund(
                                                        fund.scheme_name,   // fund name
                                                        fundIndex,
                                                        fund.scheme_code      // mf_id
                                                    );
                                                })}
                                            </div>

                                            <Input
                                                value={isNaN(fundData.amount) ? "" : fundData.amount}
                                                type="number"
                                                placeholder="Enter amount"
                                                className="p-2 w-180px"
                                                disabled={actionValue === "CANCEL"}
                                                valueChange={(input_value) => {
                                                    _handleAmount(input_value, fundIndex);
                                                }}
                                            />

                                            <img
                                                src={require('../../assets/images/modal/delete.svg').default}
                                                alt="Delete"
                                                width={20}
                                                className="cursor-pointer"
                                                onClick={() => _handleDeleteNewFund(fundIndex)}
                                            />
                                        </>
                                    )}

                                    {!fundData.isNew && (
                                        <Input
                                            value={fundData.amount}
                                            type="number"
                                            placeholder="Enter amount"
                                            className="p-2 w-180px"
                                            disabled={actionValue === "CANCEL"}
                                            valueChange={(input_value) => {
                                                if (actionValue === "EDIT") {
                                                    _handleAmount(input_value, fundIndex);
                                                }
                                            }}
                                        />
                                    )}

                                </div>
                            </div>
                            
                        </div>
                    );
                })} */}

                {sip.sip_funds.map((fundData, fundIndex) => {

                    const actionValue =
                        typeof fundData.type === "string"
                            ? fundData.type
                            : fundData.type?.value;

                    //  NEW FUND → separate row
                    if (fundData.isNew) {
                        return (
                            <div className='row gx-2 align-items-center mb-2' key={fundIndex}>

                                <div className='col-3'></div>
                                <div className='col-1'></div>
                                <div className='col-1'></div>

                                <div className='col-12'>
                                    <div className="d-flex gap-3 align-items-center">

                                        <div
                                            ref={(el) => (searchRef.current[fundIndex] = el)}
                                            className="position-relative"
                                        >
                                            <Input
                                                type="text"
                                                placeholder="Search for fund"
                                                className="mt-0 p-2 w-300px"
                                                value={fundData.fund_name}
                                                valueChange={(value) => {
                                                    _handleNewPurchaseFund(value, fundIndex, null);
                                                    _triggerSearch(value, fundIndex);
                                                }}
                                            />

                                            {_renderDropdown(fundIndex, (fund) => {
                                                setShowDropdown(null);
                                                _handleNewPurchaseFund(
                                                    fund.scheme_name,
                                                    fundIndex,
                                                    fund.scheme_code
                                                );
                                            })}
                                        </div>

                                        <Input
                                            value={isNaN(fundData.amount) ? "" : fundData.amount}
                                            type="number"
                                            placeholder="Enter amount"
                                            className="p-2 w-180px"
                                            valueChange={(val) => _handleAmount(val, fundIndex)}
                                        />

                                        <img
                                            src={require('../../assets/images/modal/delete.svg').default}
                                            alt="Delete"
                                            width={20}
                                            className="cursor-pointer"
                                            onClick={() => _handleDeleteNewFund(fundIndex)}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // ✅ EXISTING FUND → same as before
                    return (
                        <div className='row gx-2 align-items-center mb-1' key={fundIndex}>

                            <div className='col-3'>
                                <p className='pt-2 mb-0'>{fundData.fund_name}</p>
                            </div>

                            <div className='col-1'>
                                <p className='pt-2 mb-0'>{fundData.allocation ?? "0"}%</p>
                            </div>

                            <div className='col-1'>
                                <p className='pt-2 mb-0'>
                                    {!isNaN(fundData.amount) && fundData.amount !== ""
                                        ? `₹${fundData.amount}`
                                        : "-"}
                                </p>
                            </div>

                            <div className='col-2 pt-2'>
                                <SelectBox
                                    options={sipActionOptions}
                                    value={fundData.type}
                                    isSearchable={false}
                                    minHeight="38px"
                                    placeholder="No Action Required"
                                    selectchange={(value) => _handleAction(value, fundIndex)}
                                />
                            </div>

                            <div className='col-5 pt-2'>
                                {(actionValue === "EDIT" || actionValue === "CANCEL") && (
                                    <Input
                                        value={fundData.amount}
                                        type="number"
                                        placeholder="Enter amount"
                                        className="p-2 w-180px"
                                        disabled={actionValue === "CANCEL"}
                                        valueChange={(val) => {
                                            if (actionValue === "EDIT") {
                                                _handleAmount(val, fundIndex);
                                            }
                                        }}
                                    />
                                )}
                            </div>

                        </div>
                    );
                })}

                <div className="d-flex justify-content-end mt-3">
                    <SecondaryBtn
                        name="Add new fund"
                        className="px-3 py-2"
                        handleClick={() => onAddNewFund(sipIndex)}
                    />
                </div>
            </div>
        </>


    );
});




// ─── Main Modal ──────────────────────────────────────────────────────────────
const SIPMiniRebalance = React.memo((props) => {

    const navigate = useNavigate();

    const [toastMessage, setToastMessage] = useState(null);
    const [apiLoader, setApiLoader] = useState(false);
    const [orderSent, setOrderSent] = useState(false);
    const [hasPending, setHasPending] = useState(false);
    const [pendingDetails, setPendingDetails] = useState([]);

    const [showOverrideModal, setShowOverrideModal] = useState(false);
    const [overrideType, setOverrideType] = useState(""); // CREATE / EDIT / CANCEL

    const [sips, setSips] = useState(props.data.sip_data);

    console.log('sips_rebalanced', sips);


    useEffect(() => {
        const modal = document.getElementById("sip-mini-rebalance");
        const handler = () => props.handleCloseModal?.();
        modal?.addEventListener("hidden.bs.modal", handler);
        return () => modal?.removeEventListener("hidden.bs.modal", handler);
    }, [props.handleCloseModal]);

    useEffect(() => {
        if (showOverrideModal === true) {
            const modal = new Modal(document.getElementById("override-confirmation"), {});
            modal.show();
        }
    }, [showOverrideModal]);


    useEffect(() => {
        const checkPending = async () => {
            const url = devconfig.admin + '/rebalance/has-pending-rebalance';

            const payload = JSON.stringify({
                customer_id: Number(props.id),
                is_sip_rebalnce: true
            });

            const response = await APIPost(url, payload);

            if (response.status_code === 200) {
                const data = Array.isArray(response.data) ? response.data : [response.data];
                const pendingItems = data.filter(item => item?.has_pending_rebalance || item?.is_mini_rebalance);
                setHasPending(pendingItems.length > 0);
                setPendingDetails(pendingItems);
            }
        };

        checkPending();
    }, [props.id]);

    useEffect(() => {
        if (toastMessage !== null) {
            const t = setTimeout(() => setToastMessage(null), 4000);
            return () => clearTimeout(t);
        }
    }, [toastMessage]);

    function _handleFundsChange(sipIndex, updatedFunds) {
        setSips((prev) =>
            prev.map((sip, i) =>
                i === sipIndex ? { ...sip, sip_funds: updatedFunds } : sip
            )
        );
    };

    function _getOverrideType() {
        let hasCreate = false;
        let hasEdit = false;
        let hasCancel = false;

        for (const sip of sips) {
            for (const fund of sip.sip_funds) {
                if (fund.isNew) hasCreate = true;

                if (fund.type?.value === "EDIT") hasEdit = true;

                if (fund.type?.value === "CANCEL") hasCancel = true;
            }
        }

        const activeTypes = [hasCreate, hasEdit, hasCancel].filter(Boolean).length;


        if (activeTypes > 1) return "MULTIPLE";

        // Priority (you can tweak if needed)
        if (hasCreate) return "CREATE";
        if (hasEdit) return "EDIT";
        if (hasCancel) return "CANCEL";

        return "";
    }

    function _isFormValid() {
        let hasAction = false;

        for (const sip of sips) {
            for (const fund of sip.sip_funds) {
                const action = fund.type?.value;

                if (fund.isNew) {
                    if (!fund.fund_name || !fund.mf_id || !fund.amount) {
                        return false;
                    }
                    hasAction = true;
                }

                if (action === "EDIT") {
                    if (!fund.amount || fund.amount <= 0) {
                        return false;
                    }
                    hasAction = true;
                }

                if (action === "CANCEL") {
                    hasAction = true;
                }
            }
        }

        return hasAction;
    }

    // function _handleAddNewFund(sipIndex) {
    //     setSips((prev) =>
    //         prev.map((sip, i) => {
    //             if (i !== sipIndex) return sip;

    //             return {
    //                 ...sip,
    //                 sip_funds: [
    //                     ...sip.sip_funds,
    //                     {
    //                         sip_detail_id: null,
    //                         mf_id: null,
    //                         fund_name: "",
    //                         amount: "",
    //                         allocation: null,
    //                         isNew: true,
    //                     },
    //                 ],
    //             };
    //         })
    //     );
    // }

    function _handleAddNewFund(sipIndex) {
        setSips((prev) =>
            prev.map((sip, i) => {
                if (i !== sipIndex) return sip;

                // prevent multiple empty rows
                const alreadyHasEmpty = sip.sip_funds.some(f => f.isNew && !f.fund_name);

                if (alreadyHasEmpty) return sip;

                return {
                    ...sip,
                    sip_funds: [
                        ...sip.sip_funds,
                        {
                            sip_detail_id: null,
                            mf_id: null,
                            fund_name: "",
                            amount: "",
                            allocation: null,
                            isNew: true,
                        },
                    ],
                };
            })
        );
    }

    function _validateForm() {
        let hasAction = false;

        for (const sip of sips) {
            for (const fund of sip.sip_funds) {

                const action = fund.type?.value;

                // New fund validation
                if (fund.isNew) {
                    if (!fund.fund_name || !fund.mf_id || !fund.amount) {
                        setToastMessage({
                            type: "error",
                            message: "Please fill all new fund details",
                        });
                        return false;
                    }
                    hasAction = true;
                }

                // Existing fund validation
                if (action === "EDIT") {
                    if (!fund.amount || fund.amount <= 0) {
                        setToastMessage({
                            type: "error",
                            message: "Enter valid amount for edited funds",
                        });
                        return false;
                    }
                    hasAction = true;
                }

                if (action === "CANCEL") {
                    hasAction = true;
                }
            }
        }

        if (!hasAction) {
            setToastMessage({
                type: "error",
                message: "Please select at least one action",
            });
            return false;
        }

        return true;
    }

    function getActionType(fund) {
        if (fund.isNew) return "CREATE_SIP";

        if (fund.type?.value === "EDIT") return "EDIT_SIP";

        if (fund.type?.value === "CANCEL") return "CANCEL_SIP";

        return null;
    }

    const _handleTriggerRebalance = () => {


        if (!_validateForm()) return;

        if (hasPending) {
            const type = _getOverrideType();
            setOverrideType(type);
            setShowOverrideModal(true);
            return; //  stop here
        }


        _callRebalance();
    };




    function _viewOrders() {
        navigate("/rebalance-history");
        setOrderSent(false);
    }

    function _handleClose() {
        setOrderSent(false);
        props.updateOrder?.();
    }

    const _handleCloseModal = () => {
        document.getElementById("close-sip-mr")?.dispatchEvent(new Event("click"));
    };

    // const isDisabled = hasPending;
    // ── Empty state: no funds at all ─────────────────────────────────────────
    // const hasFunds = sips.some((s) => s.sip_funds.length > 0);

    const hasCreate = sips.some(sip =>
        sip.sip_funds.some(fund => fund.isNew)
    );


    const _callRebalance = () => {
        setApiLoader(true);

        const url = devconfig.admin + '/rebalance/sip-rebalance';

        let selectedSip = null;
        let rebalanceData = [];

        for (const sip of sips) {
            const editedFunds = sip.sip_funds.filter(f => f.isEdited);

            if (editedFunds.length > 0) {
                selectedSip = sip;

                rebalanceData = editedFunds.map(f => ({
                    sip_detail_id: f.sip_detail_id,
                    amount: f.amount,
                    type: getActionType(f),   //  mapping function
                    purchase_scheme: f.isNew ? f.mf_id : null
                }));

                break; // assuming one SIP at a time
            }
        }

        const payload = JSON.stringify({
            customer_id: Number(props.id),
            sip_id: selectedSip?.sip_id,
            rebalance_data: rebalanceData
        });

        APIPost(url, payload).then((response) => {
            if (response.status_code === 200) {
                setOrderSent(true);
            } else {
                setToastMessage({ type: 'error', message: response.message });
            }
            setApiLoader(false);
        });
    }

    return (
        <>
            <div
                className={`modal fade ${style.e_rebalance}`}
                id="sip-mini-rebalance"
                tabIndex="-1"
                aria-labelledby="sip-mini-rebalance"
                aria-hidden="true"
                data-bs-keyboard="false"
            >
                {orderSent ? (
                    /* ── Success screen ── */
                    <div className={`${style.e_modal_dialog} modal-dialog modal-dialog-centered`}>
                        <div className="modal-content p-4 text-center">
                            <img
                                src={require("../../assets/images/modal/successs.png").default}
                                alt="success"
                                width={62}
                                height={62}
                                className='mx-auto mt-1'
                            />
                            <h6 className='pt-4 mt-3 mb-4 pb-2'>
                                {props.message ?? "Rebalance Triggerred Successfully"}
                            </h6>
                            <div className="d-flex justify-content-center gap-3 pb-1">
                                <SecondaryBtn
                                    name="View orders"
                                    className='e-modal-submit'
                                    cancel="modal"
                                    handleClick={_viewOrders}
                                />
                                <SecondaryBtn
                                    name="Close"
                                    className={`${style.e_cancel} e-cancel`}
                                    cancel="modal"
                                    handleClick={_handleClose}
                                />
                            </div>
                        </div>
                    </div>

                ) : (
                    /* ── Main modal ── */
                    <div className={`modal-dialog position-relative modal-dialog-centered ${style.e_dialog}`}>
                        <div className={`modal-content ${style.e_content}`}>

                            {/* Header */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className={`${style.e_title} mb-0`}>SIP Mini Rebalance</h6>
                                <button
                                    type="button"
                                    id="close-sip-mr"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    style={{ width: "8px", height: "8px" }}
                                    onClick={_handleCloseModal}
                                />
                            </div>

                            {hasPending && pendingDetails.length > 0 && (
                                <div className="alert alert-warning mb-3 py-3 px-3">
                                    <strong style={{ fontSize: '15px' }}>There is already a rebalance request pending approval.</strong>
                                    {pendingDetails.map((item, index) => (
                                        <div key={index} className="mt-3" style={{ fontSize: '13px', lineHeight: '1.8' }}>
                                            <div><strong>Order Type:</strong> {item.is_mini_rebalance ? 'SIP Mini Rebalance' : 'Rebalance'}</div>
                                            {item.basket_name && <div><strong>Basket Name:</strong> {item.basket_name}</div>}
                                            {item.order_date && <div><strong>Order Date:</strong> {item.order_date}</div>}
                                        </div>
                                    ))}
                                </div>
                            )}


                            {/* Scrollable SIP / fund list */}
                            <div className={style.e_min_height} id="e-sip-funds">
                                {!sips?.length > 0 ? (
                                    <p className="text-center text-muted py-4">No funds available for rebalance.</p>
                                ) : (
                                    sips.map((sip, sipIndex) => (
                                        <SIPBlock
                                            key={sip.sip_id ?? sipIndex}
                                            sip={sip}
                                            sipIndex={sipIndex}
                                            onFundsChange={_handleFundsChange}
                                            onAddNewFund={_handleAddNewFund}
                                            toastRef={setToastMessage}
                                        />
                                    ))
                                )}
                            </div>

                            {/* Footer */}
                            <div className="row pt-3 align-items-center">
                                <div className='col-4 offset-8 d-flex justify-content-end'>
                                    <PrimaryBtn
                                        name="Trigger Rebalance"
                                        handleClick={_handleTriggerRebalance}
                                        className={
                                            _isFormValid()
                                                ? apiLoader
                                                    ? "e-disabled e-btn-loader w-100 py-3"
                                                    : "w-100 py-3"
                                                : "e-disabled py-3 w-100"
                                        }
                                    />
                                </div>
                            </div>

                        </div>
                    </div>
                )}
            </div>

            {toastMessage !== null && (
                <Toast data={toastMessage.message} type={toastMessage.type} id="3" />
            )}

            {showOverrideModal === true &&
                <OverrideConfirmation
                    show={showOverrideModal}
                    type={overrideType}
                    closeModal={() => {
                        setShowOverrideModal(false);

                        // Remove any extra untagged backdrops beyond the first (SIP modal's own)
                        const untagged = document.querySelectorAll('.modal-backdrop:not([data-owner])');
                        untagged.forEach((bd, index) => {
                            if (index > 0) bd.remove();
                        });
                    }}
                    onConfirm={() => {
                        setShowOverrideModal(false);
                        _callRebalance();
                    }}
                />
            }
        </>
    );
});


export default SIPMiniRebalance;
SIPMiniRebalance.defaultProps = {};